import {
    useState,
    useEffect,
    ChangeEvent,
    Dispatch,
    SetStateAction
} from 'react';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';

import theme from '@styles/theme';
import { isEmail, onlyNumber } from '@utils/common';
import { validateSystemOperator } from '@utils/validation';
import { Mutation } from '@graphql/types';
import { CREATE_ADMIN, UPDATE_ADMIN } from '@graphql/mutations';
import { SEE_ADMIN_LIST, SEE_ADMIN_DETAIL } from '@graphql/queries';
import { OperatorDataType } from '@pages/Setting/Sysop/Detail';

import { FlexBox } from '@components/styled/StyledComponents';
import StyledModal from '@components/styled/StyledModal';
import StyledInput from '@components/styled/StyledInput';
import StyledButton from '@components/styled/StyledButton';
import { toast } from 'react-toastify';

type OperatorModalProps = {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    data?: OperatorDataType;
};

const OperatorModal = ({ isOpen, setIsOpen, data }: OperatorModalProps) => {
    const [loading, setLoading] = useState(false);
    const [emailValMsg, setEmailValMsg] = useState('');
    const [inputs, setInputs] = useState<OperatorDataType>({
        adminId: null,
        account: '',
        name: '',
        cellphone: '',
        rank: '',
        memo: ''
    });

    const [createAdmin] =
        useMutation<Pick<Mutation, 'createAdmin'>>(CREATE_ADMIN);
    const [updateAdmin] =
        useMutation<Pick<Mutation, 'updateAdmin'>>(UPDATE_ADMIN);

    useEffect(() => {
        if (data) {
            setInputs(data);
        }
    }, [data]);

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (name === 'account') {
            if (!isEmail(value))
                setEmailValMsg('유효한 메일주소를 입력해주세요.');
            else setEmailValMsg('');
        } else if (name === 'cellphone') {
            return setInputs((prev) => ({
                ...prev,
                [name]: onlyNumber(value)
            }));
        }

        setInputs((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const onSubmit = async () => {
        const { adminId, account, name, cellphone, rank, memo } = inputs;
        if (!validateSystemOperator(account, name, cellphone)) return;

        setLoading(true);

        if (!data) {
            // 운영자 생성
            try {
                await createAdmin({
                    variables: {
                        account,
                        name,
                        cellphone,
                        rank,
                        memo
                    },
                    onCompleted: () => {
                        toast.success(`운영자를 추가했습니다.`);
                        setIsOpen(false);
                    },
                    refetchQueries: [SEE_ADMIN_LIST]
                });
            } catch (err) {
                console.error(err);
            } finally {
                return setLoading(false);
            }
        } else {
            // 운영자 수정
            try {
                await updateAdmin({
                    variables: {
                        adminId,
                        account,
                        name,
                        cellphone,
                        rank,
                        memo
                    },
                    onCompleted: () => {
                        toast.success(`운영자 정보를 수정했습니다.`);
                        setIsOpen(false);
                    },
                    refetchQueries: [SEE_ADMIN_DETAIL]
                });
            } catch (err) {
                console.error(err);
            } finally {
                return setLoading(false);
            }
        }
    };

    return (
        <StyledModal
            title={`운영자 ${!data ? '추가' : '수정'}`}
            isOpen={isOpen}
            closeBtnHandler={() => setIsOpen(false)}
            marginBottom={32}
        >
            <StyledInput
                name="account"
                title="계정명(이메일)"
                value={inputs.account}
                onChange={onChange}
                placeholder="이메일 입력"
                description={emailValMsg}
                fontColor={theme.colors.activeOrange}
                margin="0 0 24px"
                COLUMN
                REQUIRED={!data ? true : false}
                disabled={data ? true : false}
            />
            <StyledInput
                name="name"
                title="이름"
                value={inputs.name}
                onChange={onChange}
                placeholder="이름 입력"
                margin="0 0 24px"
                COLUMN
                REQUIRED={!data ? true : false}
            />
            <StyledInput
                name="cellphone"
                title="휴대폰번호"
                value={inputs.cellphone}
                onChange={onChange}
                maxLength={11}
                placeholder="휴대폰번호 입력"
                margin="0 0 24px"
                COLUMN
                REQUIRED={!data ? true : false}
            />
            <StyledInput
                name="rank"
                title="직책"
                value={inputs.rank}
                onChange={onChange}
                subTitle={!data ? '선택' : ''}
                placeholder="직책 입력"
                margin="0 0 24px"
                COLUMN
            />
            <StyledInput
                name="memo"
                title="관리자 메모"
                value={inputs.memo}
                onChange={onChange}
                subTitle={!data ? '선택' : ''}
                placeholder="관리자 메모 입력"
                margin="0 0 24px"
                COLUMN
            />
            {!data && (
                <Notice>
                    <NoticeList>
                        - 입력한 메일주소로 최초 비밀번호가 전송됩니다.
                    </NoticeList>
                    <NoticeList>
                        - 초기 로그인 한 후 최초 1회 비밀번호 재설정이
                        필요합니다.
                    </NoticeList>
                </Notice>
            )}
            <FlexBox $gap={8} $justifyContent="flex-end" $margin="130px 0 0">
                <StyledButton
                    title="취소"
                    width={140}
                    bgColor={theme.colors.whiteColor}
                    fontColor={theme.colors.blackColor}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    onClick={() => setIsOpen(false)}
                />
                <StyledButton
                    title={!data ? '추가' : '수정'}
                    width={140}
                    bgColor={
                        !data
                            ? theme.colors.blackColor
                            : theme.colors.pointGreen
                    }
                    onClick={onSubmit}
                    loading={loading}
                />
            </FlexBox>
        </StyledModal>
    );
};

export default OperatorModal;

const Notice = styled.ul``;
const NoticeList = styled.li`
    ${theme.typography.bodyMobile}
    color: ${theme.colors.deepGrayFontColor}
`;
